import { cva, cx } from 'cva'
import Image from 'next/image'
import Link from 'next/link'
import type { FC } from 'react'
import type { ImageData, Maybe } from '../../frontend-types'
import { PlaceholderImage } from './PlaceholderImage'

export type CardArticleType = {
	slug: string
	title: string
	category: Maybe<string>
	image?: ImageData
	date: string
	url: string
}

type CardArticleProps = {
	cardArticle: CardArticleType
	// primary: white-bg, secondary: blue-bg
	theme?: 'primary' | 'secondary'
}

export const textVariants = cva({
	variants: {
		theme: {
			primary:
				'text-blue hover:text-blue-shade-40 active:text-blue-shade-70 group-hover:text-blue-shade-40 group-active:text-blue-shade-70',
			white:
				'text-white hover:text-blue-tint-10 active:text-blue-tint-10 group-hover:text-blue-tint-10 group-active:text-blue-tint-10',
		},
	},
	defaultVariants: {
		theme: 'primary',
	},
})

export const CardArticle: FC<CardArticleProps> = ({ cardArticle, theme = 'primary' }) => {
	const { title, category, image, date, url } = cardArticle

	return (
		<Link
			href={url || '/'}
			target="_self"
			className={cx(
				'flex h-full flex-row gap-x-xl pb-xl tablet-l:flex-col tablet-l:pb-lg group relative',
			)}
		>
			<div className="w-1/4 flex-shrink-0 tablet-l:w-full">
				<div
					className="relative aspect-square tablet-l:aspect-video overflow-hidden rounded-l-sm tablet-l:rounded-t-sm tablet-l:rounded-bl-none"
					aria-hidden="true"
				>
					{image ? (
						<Image
							src={image.src}
							alt={title || image.alt}
							fill
							className="object-cover group-hover:scale-110 transition-transform ease-in-out duration-300"
							sizes="(max-width: 767px) 25vw, (max-width: 1023px) 20vw, (max-width: 1279px) 25vw, (max-width: 1559px) 25vw, 336px"
						/>
					) : (
						<PlaceholderImage />
					)}
				</div>
			</div>
			<div className="flex flex-col justify-between tablet-l:mt-xl flex-1">
				<div>
					{category && (
						<span
							className={cx(
								'mb-xs block text-h6',
								theme === 'primary' ? 'text-blue-shade-70' : 'text-blue-tint-10',
							)}
						>
							{category}
						</span>
					)}
					<span
						className={cx(
							'block text-h5',
							textVariants({ theme: theme === 'primary' ? 'primary' : 'white' }),
						)}
					>
						{title}
					</span>
				</div>
				<span
					className={cx(
						'block pt-2xl text-body-xs mt-auto',
						theme === 'primary' ? 'text-blue-shade-70' : 'text-blue-tint-40',
					)}
				>
					{date}
				</span>
			</div>
			<div
				className={cx(
					'absolute right-0 bottom-0 h-(length:--stroke-sm) left-0 pointer-events-none transition-all ease-in-out duration-200 z-15',
					'group-hover:h-(length:--stroke-md)',
					theme === 'primary'
						? 'bg-blue group-hover:bg-blue-shade-40 group-active:bg-blue-shade-70'
						: 'bg-blue-tint-10',
				)}
				aria-hidden="true"
			/>
		</Link>
	)
}
